import React, { useEffect, useState } from 'react';
import i18n from '../config';

/**
 * I18nReadyGuard - Prevents children from rendering until i18n is fully initialized
 * 
 * This component:
 * - Waits for i18n to finish loading translations
 * - Prevents "namespace not yet loaded" warnings
 * - Eliminates untranslated key flicker
 * - Optionally shows a loading state while initializing
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - Content to render after i18n is ready
 * @param {string[]} props.namespaces - Array of namespaces to wait for (default: ['common'])
 * @param {React.ReactNode} props.fallback - Optional loading component (default: null)
 * 
 * @example
 * <I18nReadyGuard namespaces={['common', 'login']}>
 *   <LoginPage />
 * </I18nReadyGuard>
 */
const I18nReadyGuard = ({ 
  children, 
  namespaces = ['common'],
  fallback = null 
}) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // If i18n is already initialized with all required namespaces, render immediately
    if (i18n.isInitialized && i18n.language) {
      const allNamespacesLoaded = namespaces.every(ns => 
        i18n.hasResourceBundle(i18n.language, ns)
      );
      
      if (allNamespacesLoaded) {
        setIsReady(true);
        return;
      }
    }

    // Wait for i18n initialization to complete
    const handleInitialized = () => {
      // Small timeout to ensure all namespaces are loaded
      setTimeout(() => {
        setIsReady(true);
      }, 50);
    };

    // If i18n is not initialized yet, wait for it
    if (!i18n.isInitialized) {
      i18n.on('initialized', handleInitialized);
    } else if (i18n.language) {
      // i18n is initialized but we need to wait for namespaces
      // The useTranslation hook in the child components will handle loading
      setIsReady(true);
    }

    // Cleanup event listener
    return () => {
      i18n.off('initialized', handleInitialized);
    };
  }, [namespaces]);

  if (!isReady) {
    return fallback;
  }

  return <>{children}</>;
};

export default I18nReadyGuard;
